// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Public surface of the core pipeline.
//
// buildGraph(source, userProps) → fully positioned and styled graph object.
//
// The returned graph has:
//   root            — root node
//   nodeById        — { [id]: node } map
//   preorderTraversal, postorderTraversal — arrays of all nodes
//   leaves          — visible leaf nodes (in preorder left-to-right order)
//   firstIndex      — root.preIndex
//   lastIndex       — root.preIndex + root.totalNodes
//   bounds          — { min: [x,y], max: [x,y] } in pixel space
//   width, height   — bounding-box size of the laid-out tree (layout space * 2)
//
// Each node carries (after the full pipeline):
//   id, name?, parent?, children?, isLeaf, branchLength,
//   totalNodes, totalLeaves, totalSubtreeLength,
//   visibleLeaves, postIndex, preIndex, isCollapsed, isHidden,
//   distanceFromRoot,
//   bx, by, angle, angleDegrees, inverted,
//   (circular only) bcx, bcy,
//   x, y, dist?, cx?, cy?,
//   (leaf only) shape, fillColour, strokeColour, label

export { parseNewick, serialiseNewick } from "./newick.js";
export * from "./constants.js";
export { defaultProps } from "./defaults.js";
export { rerootOnEdge, findMidpoint } from "./reroot.js";
export { ladderise } from "./order.js";
export {
  isOrthogonal,
  getPixelOffsets,
  getAlignLeafLabels,
  hasLeafLabels,
  getMetadataColumnWidth,
  hasMetadata,
  hasMetadataLabels,
  hasMetadataHeaders,
} from "./metadata.js";
export { buildSession, pickSessionProps, SESSION_VERSION } from "./session.js";
export {
  nextAnnotationId,
  createAnnotationEntry,
  updateAnnotationEntry,
  deleteAnnotationEntry,
} from "./annotations.js";

import { parseNewick } from "./newick.js";
import { defaultProps } from "./defaults.js";
import { traverseTree, reindexTraversal } from "./traverse.js";
import { applyTreeLayout } from "./layout.js";
import { computeDistances, scaleGraph, applyStyles } from "./graph-stages.js";

// stopAfter slices the pipeline so callers (the viewer's getGraph* getters) can
// re-run only the passes they need. Each value returns the graph immediately
// after the named pass, as a strict superset of the previous stage:
//   "traverse"  — structure only (leaves not yet collected)
//   "distances" — + computeDistances (root.distanceFromRoot, graph.leaves)
//   "layout"    — + applyTreeLayout & scaleGraph (bounds, branchScale, x/y)
//   undefined   — full pipeline including applyStyles (leaf shapes/colours)
export function buildGraph(source, userProps = {}, { stopAfter } = {}) {
  const props = { ...defaultProps, ...userProps };

  // Parse source string or accept a pre-parsed object
  let rawRoot;
  if (typeof source === "string") {
    rawRoot = parseNewick(source);
  } else {
    rawRoot = source;
  }

  // Traverse: build traversal arrays, annotate node fields
  let { nodeById, rootNode, postorderTraversal, preorderTraversal } =
    traverseTree(rawRoot);

  // Honour props.rotatedIds: reverse the children of each flagged node, then
  // rebuild the traversal arrays so layout's postorder stepOffset numbering
  // inherits the new order. Ids are assigned above (un-rotated preIndex) and
  // preserved here, so a flagged id stays valid across renders. Rotation is
  // cosmetic — topology, branch lengths and the fit box are unchanged.
  const rotatedIds = props.rotatedIds;
  if (Array.isArray(rotatedIds) && rotatedIds.length) {
    let rotated = false;
    for (const id of rotatedIds) {
      const node = nodeById[id];
      if (node && Array.isArray(node.children)) {
        node.children.reverse();
        rotated = true;
      }
    }
    if (rotated) {
      ({ postorderTraversal, preorderTraversal } = reindexTraversal(rootNode));
    }
  }

  // Build the initial graph object (shared mutable structure)
  const graph = {
    nodeById,
    root: rootNode,
    postorderTraversal,
    preorderTraversal,
    leaves: [],
    firstIndex: 0,
    lastIndex: 0,
    bounds: { min: [0, 0], max: [0, 0] },
    width: 0,
    height: 0,
  };

  if (stopAfter === "traverse") return graph;

  // Pass 1: compute distanceFromRoot, handle collapse, collect leaves
  computeDistances(graph, props);

  if (stopAfter === "distances") return graph;

  // Pass 2a: apply layout (sets bx/by/angle/angleDegrees/inverted/bcx/bcy)
  const layoutBounds = applyTreeLayout(graph, props.type, props.alignLabels);
  graph.bounds = layoutBounds;

  // Compute bounding-box dimensions (in layout space) for scale calculation
  graph.width = Math.max(-layoutBounds.min[0], layoutBounds.max[0]) * 2;
  graph.height = Math.max(-layoutBounds.min[1], layoutBounds.max[1]) * 2;

  // Drawing area: simplified form (callers can refine with label offsets)
  const padding = props.padding != null ? props.padding : 16;
  const size = props.size || { width: 300, height: 150 };
  const drawingArea = {
    width: size.width - 2 * padding,
    height: size.height - 2 * padding,
  };
  // Expose the box the tree was scaled into so getDrawingArea() can return it.
  graph.drawingArea = drawingArea;

  // Pass 2b: scale bx/by → x/y in pixel space
  scaleGraph(graph, props, drawingArea);

  if (stopAfter === "layout") return graph;

  // Pass 3: assign shapes, colours, labels
  applyStyles(graph, props);

  return graph;
}
